import { Brand, EBrand } from '@bonhams1793/bonhams-typescript'
import {
  DefaultOptions,
  HydrationBoundary,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AppProps } from 'next/app'
import getConfig from 'next/config'
import { useState } from 'react'
import { ThemeProvider } from 'styled-components'

import { AppStoreProvider } from '@nx/app-store'
import { PusherProvider } from '@nx/external/pusher'
import { FireScrollPositionProvider } from '@nx/fire/scroll-position'

import { ModalManager } from '@web/components/common/ModalManager/ModalManager'

import { getTheme } from '../helpers/utils'
import { GlobalProps } from '../types/app'
import { CurrencyConfig, UserContextProvider } from './UserInfoProvider'

const { publicRuntimeConfig } = getConfig()

export interface AppProvidersProps {
  pageProps?: AppProps['pageProps']
  globalProps?: GlobalProps
  children: React.ReactNode
  queryClientConfig?: {
    queryCache?: QueryCache
    mutationCache?: MutationCache
    defaultOptions?: DefaultOptions
  }
  brand?: EBrand
  currencyConfig?: CurrencyConfig | undefined
}

const AppProviders = ({
  pageProps,
  globalProps,
  queryClientConfig,
  children,
  brand = Brand.bonhams,
  currencyConfig,
}: AppProvidersProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        ...queryClientConfig,
        defaultOptions: {
          ...queryClientConfig?.defaultOptions,
          queries: {
            refetchOnWindowFocus: false,
            ...queryClientConfig?.defaultOptions?.queries,
          },
        },
      })
  )

  const theme = getTheme(brand)

  const pusherProps: Omit<
    React.ComponentProps<typeof PusherProvider>,
    'children'
  > = {
    pusherKey: publicRuntimeConfig.PUSHER.key,
    pusherCluster: publicRuntimeConfig.PUSHER.cluster,
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={globalProps?.dehydratedState}>
        <HydrationBoundary state={pageProps?.dehydratedState}>
          <FireScrollPositionProvider>
            <UserContextProvider value={{ currencyConfig }}>
              <AppStoreProvider ssrData={globalProps?.ssrAppStoreState || {}}>
                <ThemeProvider theme={theme}>
                  {globalProps?.isPusherEnabled ? (
                    <PusherProvider {...pusherProps}>
                      <ModalManager>{children}</ModalManager>
                    </PusherProvider>
                  ) : (
                    <ModalManager>{children}</ModalManager>
                  )}
                </ThemeProvider>
              </AppStoreProvider>
            </UserContextProvider>
          </FireScrollPositionProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </HydrationBoundary>
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

export default AppProviders
